import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import {
  FiX,
  FiCheck,
  FiAlertCircle,
  FiChevronDown,
  FiCopy,
  FiRefreshCw,
  FiEdit2,
  FiExternalLink,
  FiClock,
  FiPlay,
} from "react-icons/fi";
import {
  testGenerator,
  fetchSampleRequestFromApi,
} from "../../services/generatorService";
import {
  fetchCurricula,
  fetchSubjectsForCurriculum,
  fetchCoursesForSubject,
} from "../../services/curriculumApiService";

// Maps check names to user-friendly display info
const CHECK_CONFIG = {
  reachability: {
    label: "Endpoint Reachable",
    description: "Can the server reach your endpoint?",
    failAdvice:
      "Could not reach your endpoint. Verify the URL is correct and the server is running.",
  },
  authentication: {
    label: "Authentication Valid",
    description: "Are your credentials accepted?",
    failAdvice:
      "Check that your API key or Bearer token is correct in generator settings.",
  },
  response_format: {
    label: "Valid JSON Response",
    description: "Does the endpoint return valid JSON?",
    failAdvice:
      "Your endpoint is not returning valid JSON. It may be returning an HTML error page or plain text.",
  },
  response_schema: {
    label: "Response Structure",
    description: "Does the response match expected format?",
    failAdvice:
      "Your response must have a `generated_content` array where each item has `id`, `request`, and `content` keys with type-specific fields.",
  },
};

const TestEndpointModal = ({ generator, onClose, onEditGenerator }) => {
  // Phase state: "preview" or "testing"
  const [phase, setPhase] = useState("preview");

  // Preview state
  const [previewPayload, setPreviewPayload] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [previewError, setPreviewError] = useState(null);

  // Filter state — every field defaults to "" ("Any"). Unconstrained fields
  // are randomly resolved against the curriculum API when the sample request
  // is built.
  const [filterCurriculum, setFilterCurriculum] = useState("");
  const [filterSubject, setFilterSubject] = useState("");
  const [filterCourse, setFilterCourse] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterDifficulty, setFilterDifficulty] = useState("");

  // Dropdown options come straight from the curriculum API — no DB-side
  // benchmark sets, no hardcoded name maps. Subject/course options load
  // lazily based on the selected parent.
  const [curricula, setCurricula] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [courses, setCourses] = useState([]);

  // Content type + difficulty options — kept in sync with the Zod schema in
  // @trilogy-innovations/content-renderers (CONTENT_TYPES).
  const TYPE_OPTIONS = [
    "mcq",
    "msq",
    "fill-in",
    "match",
    "article",
    "mcq_set",
    "saq",
    "leq",
    "dbq",
  ];
  const DIFFICULTY_OPTIONS = ["easy", "medium", "hard"];

  // Test result state
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [animatedCheckIndex, setAnimatedCheckIndex] = useState(-1);
  const [animationDone, setAnimationDone] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [copiedField, setCopiedField] = useState(null);

  // Top-level curricula come from /list-curricula on mount.
  useEffect(() => {
    const loadCurricula = async () => {
      const { data } = await fetchCurricula();
      setCurricula(data || []);
    };
    loadCurricula();
  }, []);

  // Subjects depend on the selected curriculum. When curriculum is Any we
  // can't enumerate subjects without a parent, so the dropdown stays Any-only.
  useEffect(() => {
    if (!filterCurriculum) {
      setSubjects([]);
      return;
    }
    let cancelled = false;
    const loadSubjects = async () => {
      const { data } = await fetchSubjectsForCurriculum(filterCurriculum);
      if (!cancelled) {
        setSubjects(data || []);
      }
    };
    loadSubjects();
    return () => {
      cancelled = true;
    };
  }, [filterCurriculum]);

  // Courses depend on curriculum + subject.
  useEffect(() => {
    if (!filterCurriculum || !filterSubject) {
      setCourses([]);
      return;
    }
    let cancelled = false;
    const loadCourses = async () => {
      const { data } = await fetchCoursesForSubject(
        filterCurriculum,
        filterSubject
      );
      if (!cancelled) {
        setCourses(data || []);
      }
    };
    loadCourses();
    return () => {
      cancelled = true;
    };
  }, [filterCurriculum, filterSubject]);

  // Fetch sample request on mount and when filters change. Any "" filter is
  // treated as "Any" — fetchSampleRequestFromApi walks the curriculum API
  // and randomly resolves whatever the user left open.
  const loadSampleRequest = useCallback(async () => {
    setPreviewLoading(true);
    setPreviewError(null);

    const { data, error: fetchError } = await fetchSampleRequestFromApi({
      curriculum: filterCurriculum,
      subject: filterSubject,
      course: filterCourse,
      type: filterType,
      difficulty: filterDifficulty,
    });

    if (fetchError) {
      setPreviewError(
        fetchError.message ||
          "Failed to load sample request from curriculum API."
      );
      setPreviewLoading(false);
      return;
    }

    if (!data) {
      setPreviewError(
        "No substandards found for the selected curriculum / subject / course."
      );
      setPreviewLoading(false);
      return;
    }

    setPreviewPayload(data);
    setPreviewLoading(false);
  }, [
    filterCurriculum,
    filterSubject,
    filterCourse,
    filterType,
    filterDifficulty,
  ]);

  useEffect(() => {
    loadSampleRequest();
  }, [loadSampleRequest]);

  // Run the actual endpoint test
  const runTest = useCallback(async () => {
    setPhase("testing");
    setLoading(true);
    setResult(null);
    setError(null);
    setAnimatedCheckIndex(-1);
    setAnimationDone(false);
    setExpandedSections({});

    const { data, error: testError } = await testGenerator(
      generator.id,
      previewPayload
    );

    if (testError) {
      setError(testError.message || "Failed to test generator endpoint");
      setLoading(false);
      return;
    }

    setResult(data);
    setLoading(false);
  }, [generator.id, previewPayload]);

  // Back to preview
  const handleBackToPreview = () => {
    setPhase("preview");
    setResult(null);
    setError(null);
    setLoading(false);
    setAnimatedCheckIndex(-1);
    setAnimationDone(false);
  };

  // Animate checks sequentially after results arrive
  useEffect(() => {
    if (!result || !result.checks) {
      return;
    }

    let index = 0;
    setAnimatedCheckIndex(0);

    const interval = setInterval(() => {
      index++;
      if (index < result.checks.length) {
        setAnimatedCheckIndex(index);
      } else {
        setAnimationDone(true);
        clearInterval(interval);
      }
    }, 600);

    return () => clearInterval(interval);
  }, [result]);

  const toggleSection = (key) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleCopy = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const formatJson = (text) => {
    try {
      return JSON.stringify(JSON.parse(text), null, 2);
    } catch {
      return text;
    }
  };

  const passedCount = result?.checks?.filter((c) => c.passed).length || 0;
  const totalCount = result?.checks?.length || 0;
  const allPassed = result?.success;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div className="flex items-center gap-3">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {phase === "preview" ? "Test Endpoint" : "Endpoint Diagnostics"}
              </h3>
              <p className="text-sm text-gray-500">{generator.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-gray-400 transition-all duration-200 hover:bg-gray-100 hover:text-gray-600"
          >
            <FiX className="size-5" />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-auto">
          <div className="p-6">
            {/* ===== PREVIEW PHASE ===== */}
            {phase === "preview" && (
              <>
                {/* Label */}
                <p className="mb-4 text-sm text-gray-600">
                  This is a test request that will be sent to your endpoint.
                  Review it before testing.
                </p>

                {/* Filter section */}
                <div className="mb-4 rounded-lg border border-gray-200">
                  <div className="space-y-3 px-4 py-3">
                    <div className="grid grid-cols-3 gap-3">
                      {/* Curriculum */}
                      <div>
                        <label
                          htmlFor="filter-curriculum"
                          className="mb-1 block text-xs font-medium text-gray-500"
                        >
                          Curriculum
                        </label>
                        <select
                          id="filter-curriculum"
                          value={filterCurriculum}
                          onChange={(e) => {
                            setFilterCurriculum(e.target.value);
                            setFilterSubject("");
                            setFilterCourse("");
                          }}
                          className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 transition-colors focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-200"
                        >
                          <option value="">Any</option>
                          {curricula.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Subject */}
                      <div>
                        <label
                          htmlFor="filter-subject"
                          className="mb-1 block text-xs font-medium text-gray-500"
                        >
                          Subject
                        </label>
                        <select
                          id="filter-subject"
                          value={filterSubject}
                          onChange={(e) => {
                            setFilterSubject(e.target.value);
                            setFilterCourse("");
                          }}
                          disabled={!filterCurriculum}
                          className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 transition-colors focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-200 disabled:bg-gray-50 disabled:text-gray-400"
                        >
                          <option value="">Any</option>
                          {subjects.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Course */}
                      <div>
                        <label
                          htmlFor="filter-course"
                          className="mb-1 block text-xs font-medium text-gray-500"
                        >
                          Course
                        </label>
                        <select
                          id="filter-course"
                          value={filterCourse}
                          onChange={(e) => setFilterCourse(e.target.value)}
                          disabled={!filterCurriculum || !filterSubject}
                          className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 transition-colors focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-200 disabled:bg-gray-50 disabled:text-gray-400"
                        >
                          <option value="">Any</option>
                          {courses.map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {/* Type */}
                      <div>
                        <label
                          htmlFor="filter-type"
                          className="mb-1 block text-xs font-medium text-gray-500"
                        >
                          Type
                        </label>
                        <select
                          id="filter-type"
                          value={filterType}
                          onChange={(e) => setFilterType(e.target.value)}
                          className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 transition-colors focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-200"
                        >
                          <option value="">Any</option>
                          {TYPE_OPTIONS.map((t) => (
                            <option key={t} value={t}>
                              {t}
                            </option>
                          ))}
                        </select>
                      </div>
                      {/* Difficulty */}
                      <div>
                        <label
                          htmlFor="filter-difficulty"
                          className="mb-1 block text-xs font-medium text-gray-500"
                        >
                          Difficulty
                        </label>
                        <select
                          id="filter-difficulty"
                          value={filterDifficulty}
                          onChange={(e) => setFilterDifficulty(e.target.value)}
                          className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 transition-colors focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-200"
                        >
                          <option value="">Any</option>
                          {DIFFICULTY_OPTIONS.map((d) => (
                            <option key={d} value={d}>
                              {d}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Request payload preview */}
                {previewLoading ? (
                  <div className="py-6 text-center">
                    <div className="inline-block size-8 animate-spin rounded-full border-4 border-solid border-amber-500 border-r-transparent"></div>
                    <p className="mt-3 text-sm text-gray-500">
                      Loading sample request...
                    </p>
                  </div>
                ) : previewError ? (
                  <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4">
                    <FiAlertCircle className="mt-0.5 size-5 shrink-0 text-red-600" />
                    <div>
                      <p className="text-sm font-medium text-red-800">
                        {previewError}
                      </p>
                      <button
                        onClick={loadSampleRequest}
                        className="mt-1 text-sm font-medium text-red-700 hover:text-red-900 hover:underline"
                      >
                        Try again
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between border-b border-gray-100 px-4 py-2">
                      <span className="text-xs font-medium text-gray-500">
                        Request Payload
                      </span>
                      <button
                        onClick={() =>
                          handleCopy(
                            JSON.stringify(previewPayload, null, 2),
                            "preview"
                          )
                        }
                        className="flex items-center gap-1 rounded px-2 py-1 text-xs text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
                      >
                        {copiedField === "preview" ? (
                          <>
                            <FiCheck className="size-3 text-green-500" />
                            <span className="text-green-600">Copied!</span>
                          </>
                        ) : (
                          <>
                            <FiCopy className="size-3" />
                            <span>Copy</span>
                          </>
                        )}
                      </button>
                    </div>
                    <pre className="max-h-64 overflow-y-auto whitespace-pre-wrap break-words bg-gray-50 p-4 text-xs leading-relaxed text-gray-700">
                      {JSON.stringify(previewPayload, null, 2)}
                    </pre>
                  </div>
                )}
              </>
            )}

            {/* ===== TESTING PHASE ===== */}
            {phase === "testing" && (
              <>
                {/* Fatal error state */}
                {error && !result && (
                  <div className="mb-4 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4">
                    <FiAlertCircle className="mt-0.5 size-5 shrink-0 text-red-600" />
                    <div>
                      <p className="font-medium text-red-800">
                        Test could not be run
                      </p>
                      <p className="mt-1 text-sm text-red-700">{error}</p>
                    </div>
                  </div>
                )}

                {/* Loading state — show check placeholders */}
                {loading && (
                  <div className="space-y-2">
                    <div className="mb-3 flex items-center gap-2">
                      <div className="size-4 animate-spin rounded-full border-2 border-amber-500 border-r-transparent" />
                      <p className="text-sm text-gray-500">
                        Running diagnostics on your endpoint...
                      </p>
                    </div>
                    {Object.entries(CHECK_CONFIG).map(([key, config]) => (
                      <div
                        key={key}
                        className="flex items-center gap-3 rounded-lg border border-gray-100 bg-gray-50 px-4 py-3"
                      >
                        <div className="flex size-7 shrink-0 items-center justify-center">
                          <div className="size-5 rounded-full border-2 border-gray-200" />
                        </div>
                        <p className="text-sm font-medium text-gray-400">
                          {config.label}
                        </p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Diagnostic checks */}
                {result && result.checks && (
                  <>
                    <div className="space-y-2">
                      {result.checks.map((check, idx) => {
                        const config = CHECK_CONFIG[check.name] || {
                          label: check.name,
                          description: "",
                          failAdvice: "",
                        };
                        const isRevealed = animatedCheckIndex >= idx;
                        const isCurrent =
                          !animationDone && animatedCheckIndex === idx;
                        const showFailAdvice = animationDone && !check.passed;

                        return (
                          <div
                            key={check.name}
                            className={`rounded-lg border transition-all duration-300 ${
                              !isRevealed
                                ? "border-gray-100 bg-gray-50"
                                : check.passed
                                  ? "border-green-200 bg-green-50/50"
                                  : "border-red-200 bg-red-50/50"
                            }`}
                          >
                            <div className="flex items-center gap-3 px-4 py-3">
                              {/* Status icon */}
                              <div className="flex size-7 shrink-0 items-center justify-center">
                                {!isRevealed ? (
                                  <div className="size-5 rounded-full border-2 border-gray-200" />
                                ) : isCurrent ? (
                                  <div className="size-5 animate-spin rounded-full border-2 border-amber-500 border-r-transparent" />
                                ) : check.passed ? (
                                  <div className="flex size-5 items-center justify-center rounded-full bg-green-500">
                                    <FiCheck className="size-3 text-white" />
                                  </div>
                                ) : (
                                  <div className="flex size-5 items-center justify-center rounded-full bg-red-500">
                                    <FiX className="size-3 text-white" />
                                  </div>
                                )}
                              </div>

                              {/* Label and message */}
                              <div className="min-w-0 flex-1">
                                <p
                                  className={`text-sm font-medium ${
                                    !isRevealed
                                      ? "text-gray-400"
                                      : "text-gray-900"
                                  }`}
                                >
                                  {config.label}
                                </p>
                                {isRevealed && !isCurrent && (
                                  <p className="text-xs text-gray-500">
                                    {check.message}
                                  </p>
                                )}
                                {isCurrent && (
                                  <p className="text-xs text-gray-400">
                                    {config.description}
                                  </p>
                                )}
                              </div>

                              {/* Status badge */}
                              {isRevealed && !isCurrent && (
                                <span
                                  className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
                                    check.passed
                                      ? "bg-green-100 text-green-700"
                                      : "bg-red-100 text-red-700"
                                  }`}
                                >
                                  {check.passed ? "Passed" : "Failed"}
                                </span>
                              )}
                            </div>

                            {/* Expanded failure advice */}
                            {showFailAdvice && (
                              <div className="border-t border-red-200 px-4 py-3">
                                <div className="flex items-start gap-2 rounded-md bg-red-50 p-3">
                                  <FiAlertCircle className="mt-0.5 size-4 shrink-0 text-red-500" />
                                  <div className="text-sm text-red-700">
                                    <p>{config.failAdvice}</p>
                                    {check.name === "response_schema" && (
                                      <Link
                                        to="/generator-api-interface"
                                        className="mt-1 inline-flex items-center gap-1 font-medium text-red-800 hover:text-red-900 hover:underline"
                                      >
                                        View API Interface Spec
                                        <FiExternalLink className="size-3" />
                                      </Link>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Summary bar */}
                    {animationDone && (
                      <div
                        className={`mt-4 flex items-center gap-3 rounded-lg border p-4 ${
                          allPassed
                            ? "border-green-200 bg-green-50"
                            : "border-red-200 bg-red-50"
                        }`}
                      >
                        {allPassed ? (
                          <FiCheck className="size-5 shrink-0 text-green-600" />
                        ) : (
                          <FiAlertCircle className="size-5 shrink-0 text-red-600" />
                        )}
                        <div className="flex-1">
                          <p
                            className={`text-sm font-medium ${
                              allPassed ? "text-green-800" : "text-red-800"
                            }`}
                          >
                            {allPassed
                              ? "All checks passed"
                              : `${totalCount - passedCount} of ${totalCount} check${totalCount - passedCount !== 1 ? "s" : ""} failed`}
                          </p>
                        </div>
                        <div className="flex items-center gap-1.5 text-sm text-gray-500">
                          <FiClock className="size-3.5" />
                          <span>
                            {(result.responseTimeMs / 1000).toFixed(2)}s
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Request / Response sections (only after animation) */}
                    {animationDone && (
                      <div className="mt-6 space-y-3">
                        {/* Request Sent */}
                        {result.requestPayload && (
                          <div className="rounded-lg border border-gray-200">
                            <button
                              onClick={() => toggleSection("request")}
                              className="flex w-full items-center justify-between px-4 py-3 text-left transition-colors hover:bg-gray-50"
                            >
                              <span className="text-sm font-medium text-gray-700">
                                Request Sent
                              </span>
                              <FiChevronDown
                                className={`size-4 text-gray-400 transition-transform ${
                                  expandedSections.request ? "rotate-180" : ""
                                }`}
                              />
                            </button>
                            {expandedSections.request && (
                              <div className="border-t border-gray-200">
                                <div className="flex items-center justify-between border-b border-gray-100 px-4 py-2">
                                  <span className="text-xs text-gray-400">
                                    JSON payload sent to your endpoint
                                  </span>
                                  <button
                                    onClick={() =>
                                      handleCopy(
                                        JSON.stringify(
                                          result.requestPayload,
                                          null,
                                          2
                                        ),
                                        "request"
                                      )
                                    }
                                    className="flex items-center gap-1 rounded px-2 py-1 text-xs text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
                                  >
                                    {copiedField === "request" ? (
                                      <>
                                        <FiCheck className="size-3 text-green-500" />
                                        <span className="text-green-600">
                                          Copied!
                                        </span>
                                      </>
                                    ) : (
                                      <>
                                        <FiCopy className="size-3" />
                                        <span>Copy</span>
                                      </>
                                    )}
                                  </button>
                                </div>
                                <pre className="max-h-60 overflow-auto p-4 text-xs text-gray-700">
                                  {JSON.stringify(
                                    result.requestPayload,
                                    null,
                                    2
                                  )}
                                </pre>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Response Received */}
                        {result.responseBody !== undefined && (
                          <div className="rounded-lg border border-gray-200">
                            <button
                              onClick={() => toggleSection("response")}
                              className="flex w-full items-center justify-between px-4 py-3 text-left transition-colors hover:bg-gray-50"
                            >
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-gray-700">
                                  Response Received
                                </span>
                                {result.statusCode && (
                                  <span
                                    className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                                      result.statusCode >= 200 &&
                                      result.statusCode < 300
                                        ? "bg-green-100 text-green-700"
                                        : result.statusCode >= 400
                                          ? "bg-red-100 text-red-700"
                                          : "bg-yellow-100 text-yellow-700"
                                    }`}
                                  >
                                    {result.statusCode}
                                  </span>
                                )}
                              </div>
                              <FiChevronDown
                                className={`size-4 text-gray-400 transition-transform ${
                                  expandedSections.response ? "rotate-180" : ""
                                }`}
                              />
                            </button>
                            {expandedSections.response && (
                              <div className="border-t border-gray-200">
                                <div className="flex items-center justify-between border-b border-gray-100 px-4 py-2">
                                  <span className="text-xs text-gray-400">
                                    {result.responseBody
                                      ? result.responseBody.length > 2000
                                        ? `Showing first 2000 of ${result.responseBody.length} characters — use Copy for full response`
                                        : `${result.responseBody.length} characters`
                                      : "No response body"}
                                  </span>
                                  {result.responseBody && (
                                    <button
                                      onClick={() =>
                                        handleCopy(
                                          result.responseBody,
                                          "response"
                                        )
                                      }
                                      className="flex items-center gap-1 rounded px-2 py-1 text-xs text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
                                    >
                                      {copiedField === "response" ? (
                                        <>
                                          <FiCheck className="size-3 text-green-500" />
                                          <span className="text-green-600">
                                            Copied!
                                          </span>
                                        </>
                                      ) : (
                                        <>
                                          <FiCopy className="size-3" />
                                          <span>Copy full</span>
                                        </>
                                      )}
                                    </button>
                                  )}
                                </div>
                                <pre className="max-h-60 overflow-auto p-4 text-xs text-gray-700">
                                  {result.responseBody
                                    ? formatJson(
                                        result.responseBody.slice(0, 2000)
                                      )
                                    : "No response body received"}
                                </pre>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Curl Command */}
                        {result.curlCommand && (
                          <div className="rounded-lg border border-gray-200">
                            <div className="flex items-center justify-between px-4 py-3">
                              <span className="text-sm font-medium text-gray-700">
                                cURL Command
                              </span>
                              <button
                                onClick={() =>
                                  handleCopy(result.curlCommand, "curl")
                                }
                                className="flex items-center gap-1.5 rounded-md bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-200 hover:text-gray-800"
                              >
                                {copiedField === "curl" ? (
                                  <>
                                    <FiCheck className="size-3 text-green-500" />
                                    <span className="text-green-600">
                                      Copied!
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    <FiCopy className="size-3" />
                                    <span>Copy</span>
                                  </>
                                )}
                              </button>
                            </div>
                            <div className="rounded-b-lg bg-gray-900 p-4">
                              <pre className="whitespace-pre-wrap break-all text-xs leading-relaxed text-gray-100">
                                {result.curlCommand}
                              </pre>
                            </div>
                            <div className="border-t border-gray-200 px-4 py-2">
                              <p className="text-xs text-gray-400">
                                Credential is replaced with a placeholder for
                                security. Use your actual key when running
                                manually.
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-gray-200 px-6 py-4">
          <div>
            {phase === "testing" && !loading && (
              <button
                onClick={handleBackToPreview}
                className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition-all duration-200 hover:border-gray-400 hover:bg-gray-50"
              >
                <FiRefreshCw className="size-4" />
                <span>New Test</span>
              </button>
            )}
          </div>

          <div className="flex items-center gap-3">
            {/* Preview phase: Test button */}
            {phase === "preview" && (
              <button
                onClick={runTest}
                disabled={previewLoading || !!previewError}
                className="flex items-center gap-2 rounded-lg bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <FiPlay className="size-4" />
                <span>Test Endpoint</span>
              </button>
            )}

            {/* Testing phase: Edit Generator button on failure */}
            {phase === "testing" &&
              animationDone &&
              !allPassed &&
              onEditGenerator && (
                <button
                  onClick={() => {
                    onClose();
                    onEditGenerator(generator);
                  }}
                  className="flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:bg-primary-700"
                >
                  <FiEdit2 className="size-4" />
                  <span>Edit Generator</span>
                </button>
              )}

            <button
              onClick={onClose}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition-all duration-200 hover:border-gray-400 hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

TestEndpointModal.propTypes = {
  generator: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    endpoint_url: PropTypes.string,
    auth_type: PropTypes.oneOf(["none", "bearer", "x-api-key"]),
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onEditGenerator: PropTypes.func,
};

export default TestEndpointModal;
